import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FiAlertCircle,
  FiCalendar,
  FiClock,
  FiEdit2,
  FiPlus,
  FiTrash2,
  FiSearch,
  FiUser,
  FiXCircle,
  FiChevronDown,
  FiChevronRight,
} from "react-icons/fi";
import Spinner from "../../components/Spinner";
import Modal from "../../components/Modal";
import {
  LdsOverviewHero,
  LdsOverviewPage,
} from "../../components/lds/LdsOverview";
import http from "../../api/apiClient";
import { translateClassName } from "../../utils/classroomUtils";
import { useCan } from "../../authz/useCan";

const IconAlert = FiAlertCircle as any;
const IconCalendar = FiCalendar as any;
const IconClock = FiClock as any;
const IconEdit = FiEdit2 as any;
const IconPlus = FiPlus as any;
const IconSearch = FiSearch as any;
const IconTrash = FiTrash2 as any;
const IconUser = FiUser as any;
const IconCancel = FiXCircle as any;
const IconChevronDown = FiChevronDown as any;
const IconChevronRight = FiChevronRight as any;

const parentMeetingsStyles = `
  @keyframes myParentMeetingsEnter {
    from { opacity: 0; transform: translateY(14px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .my-parent-meetings-enter {
    animation: myParentMeetingsEnter 420ms cubic-bezier(0.22, 1, 0.36, 1);
    animation-fill-mode: both;
  }

  @media (prefers-reduced-motion: reduce) {
    .my-parent-meetings-enter { animation: none !important; }
  }
`;

const lumenPageShell = "flex flex-col gap-6 text-gray-900 dark:text-gray-100";
const lumenPanel = "rounded-[32px] border border-indigo-100/60 bg-gradient-to-br from-indigo-50/90 via-white to-white shadow-[0_25px_70px_rgba(15,23,42,0.12)] backdrop-blur dark:border-indigo-500/40 dark:bg-gray-900/60 dark:from-gray-900/70 dark:via-indigo-950/40 dark:to-gray-900/70";
const lumenSecondaryButton = "lumen-glow-sheen inline-flex items-center justify-center rounded-full border border-gray-200/70 bg-white/80 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600/40 dark:bg-gray-900/40 dark:text-gray-200";
const lumenIndigoButton = "lumen-glow-sheen inline-flex items-center justify-center rounded-full border border-indigo-200/70 bg-indigo-50/80 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:text-indigo-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200";
const lumenSkyButton = "lumen-glow-sheen inline-flex items-center justify-center rounded-full border border-sky-200/70 bg-sky-50/80 px-4 py-2 text-sm font-semibold text-sky-700 transition hover:text-sky-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-sky-500/50 dark:bg-sky-900/40 dark:text-sky-200";
const lumenEmeraldButton = "lumen-glow-sheen inline-flex items-center justify-center rounded-full border border-emerald-200/70 bg-emerald-50/80 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:text-emerald-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-500/50 dark:bg-emerald-950/40 dark:text-emerald-200";

type ParentMeetingSlot = {
  slotId?: string;
  id?: string;
  startTime?: unknown;
  startsAt?: unknown;
  startDateTime?: unknown;
  start?: unknown;
  endTime?: unknown;
  endsAt?: unknown;
  endDateTime?: unknown;
  end?: unknown;
  status?: string;
  childUuid?: string;
  childName?: string;
  parentUuid?: string;
  parentName?: string;
  parentEmail?: string;
  invitationId?: string;
  location?: string;
};

type ParentMeetingOverview = {
  planId?: string;
  id?: string;
  title?: string;
  classroomUuid?: string;
  classroomId?: string;
  classroomName?: string;
  status?: string;
  openSlots?: ParentMeetingSlot[];
  plannedMeetings?: ParentMeetingSlot[];
};

type SlotModalState =
  | { type: "add"; plan: ParentMeetingOverview }
  | { type: "edit"; plan: ParentMeetingOverview; slot: ParentMeetingSlot }
  | { type: "delete"; plan: ParentMeetingOverview; slot: ParentMeetingSlot }
  | { type: "cancel"; plan: ParentMeetingOverview; slot: ParentMeetingSlot }
  | { type: "deletePlan"; plan: ParentMeetingOverview }
  | null;

const getPlanId = (plan: ParentMeetingOverview): string => plan.planId || plan.id || "";
const getClassroomId = (plan: ParentMeetingOverview): string => plan.classroomUuid || plan.classroomId || "";
const getDisplayClassroomName = (plan: ParentMeetingOverview): string => translateClassName(plan.classroomName || "") || plan.classroomName || "";
const getSlotId = (slot: ParentMeetingSlot): string => slot.slotId || slot.id || "";
const getSlotStart = (slot: ParentMeetingSlot): unknown =>
  slot.startTime ?? slot.startsAt ?? slot.startDateTime ?? slot.start ?? null;
const getSlotEnd = (slot: ParentMeetingSlot): unknown =>
  slot.endTime ?? slot.endsAt ?? slot.endDateTime ?? slot.end ?? null;

const isValidMeetingDate = (date: Date): boolean => {
  const time = date.getTime();
  return Number.isFinite(time) && date.getFullYear() >= 2000;
};

const parseMeetingDateTime = (value: unknown): Date | null => {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    return isValidMeetingDate(value) ? value : null;
  }

  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value.map(Number);
    if ([year, month, day, hour, minute, second].some((part) => !Number.isFinite(part))) return null;
    const parsed = new Date(year, month - 1, day, hour, minute, second);
    return isValidMeetingDate(parsed) ? parsed : null;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value) || value <= 0) return null;
    const millis = value < 1_000_000_000_000 ? value * 1000 : value;
    const parsed = new Date(millis);
    return isValidMeetingDate(parsed) ? parsed : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^\d+$/.test(trimmed)) {
      return parseMeetingDateTime(Number(trimmed));
    }

    const parsed = new Date(trimmed);
    return isValidMeetingDate(parsed) ? parsed : null;
  }

  return null;
};

const formatDate = (value: unknown): string => {
  const parsed = parseMeetingDateTime(value);
  if (!parsed) return "";
  return parsed.toLocaleDateString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatTime = (value: unknown): string => {
  const parsed = parseMeetingDateTime(value);
  if (!parsed) return "";
  return parsed.toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toDateTimeInputValue = (value: unknown): string => {
  const parsed = parseMeetingDateTime(value);
  if (!parsed) return "";
  const pad = (part: number) => String(part).padStart(2, "0");
  return [
    parsed.getFullYear(),
    pad(parsed.getMonth() + 1),
    pad(parsed.getDate()),
  ].join("-") + `T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

const toRequestDateTime = (value: string): string => new Date(value).toISOString();

const isAvailableSlot = (slot: ParentMeetingSlot): boolean => String(slot.status || "").toUpperCase() === "AVAILABLE";

type SlotDateGroup = {
  dateLabel: string;
  sortTime: number;
  slots: ParentMeetingSlot[];
};

type EnrichedSlotDateGroup = SlotDateGroup & {
  openCount: number;
  plannedCount: number;
};

const slotSortTime = (slot: ParentMeetingSlot): number => parseMeetingDateTime(getSlotStart(slot))?.getTime() ?? Number.MAX_SAFE_INTEGER;

const dedupeSlotsForDisplay = (slots: ParentMeetingSlot[], planId: string, dedupeAvailableOnly = false): ParentMeetingSlot[] => {
  const seen = new Set<string>();
  return slots.filter((slot) => {
    if (dedupeAvailableOnly && !isAvailableSlot(slot)) return true;
    const start = parseMeetingDateTime(getSlotStart(slot))?.getTime() ?? String(getSlotStart(slot));
    const end = parseMeetingDateTime(getSlotEnd(slot))?.getTime() ?? String(getSlotEnd(slot));
    const status = String(slot.status || "").toUpperCase();
    const key = `${planId}:${start}:${end}:${status}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const groupSlotsByDate = (slots: ParentMeetingSlot[]): SlotDateGroup[] => {
  const groups = new Map<string, SlotDateGroup>();
  const unknownLabel = "Unknown time";
  const sortedSlots = [...slots].sort((left, right) => slotSortTime(left) - slotSortTime(right));

  sortedSlots.forEach((slot) => {
    const start = parseMeetingDateTime(getSlotStart(slot));
    const label = start ? formatDate(start) : unknownLabel;
    const key = start ? start.toISOString().slice(0, 10) : "unknown";
    const group = groups.get(key) || {
      dateLabel: label,
      sortTime: start?.getTime() ?? Number.MAX_SAFE_INTEGER,
      slots: [],
    };
    group.slots.push(slot);
    groups.set(key, group);
  });

  return Array.from(groups.values()).sort((left, right) => left.sortTime - right.sortTime);
};

async function listParentMeetingOverview(classroomId?: string | null): Promise<ParentMeetingOverview[]> {
  const { data } = await http.get<ParentMeetingOverview[]>("/parent-meetings/overview", {
    params: classroomId ? { classroomId } : undefined,
  });
  return Array.isArray(data) ? data : [];
}

function slotMatches(slot: ParentMeetingSlot, query: string): boolean {
  return [
    slot.childName,
    slot.parentName,
    slot.parentEmail,
    slot.status,
    slot.location,
    formatDate(getSlotStart(slot)),
    formatTime(getSlotStart(slot)),
    formatTime(getSlotEnd(slot)),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(query);
}

function planMatches(plan: ParentMeetingOverview, rawQuery: string): boolean {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return true;

  const planText = [
    plan.title,
    plan.classroomName,
    plan.status,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (planText.includes(query)) return true;
  return [...(plan.openSlots || []), ...(plan.plannedMeetings || [])].some((slot) => slotMatches(slot, query));
}

const statusLabel = (status?: string): string => status || "Available";

const MyParentMeetings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const classroomId = searchParams.get("classroomId");
  const canManageParentMeetings = useCan("my.parent-meetings", "manage");

  const [plans, setPlans] = useState<ParentMeetingOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [slotModal, setSlotModal] = useState<SlotModalState>(null);
  const [slotStart, setSlotStart] = useState("");
  const [slotEnd, setSlotEnd] = useState("");
  const [modalError, setModalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [openDayKeys, setOpenDayKeys] = useState<Record<string, boolean>>({});

  const loadMeetings = async () => {
    setLoading(true);
    setError(null);

    try {
      const rows = await listParentMeetingOverview(classroomId);
      setPlans(rows);
    } catch (err) {
      console.error("Failed to load parent meeting overview", err);
      setError(
        t("parentMeetings.load_failed", {
          defaultValue: "Parent meetings could not be loaded.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeetings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classroomId]);

  const filteredPlans = useMemo(
    () => plans.filter((plan) => planMatches(plan, query)),
    [plans, query]
  );

  const getDayKey = (planId: string, group: SlotDateGroup): string =>
    `${planId}:${group.dateLabel}:${group.sortTime}`;

  const isDayOpen = (planId: string, group: SlotDateGroup): boolean =>
    openDayKeys[getDayKey(planId, group)] ?? false;

  const toggleDay = (planId: string, group: SlotDateGroup) => {
    const key = getDayKey(planId, group);
    setOpenDayKeys((current) => ({
      ...current,
      [key]: !(current[key] ?? false),
    }));
  };

  const closeSlotModal = () => {
    setSlotModal(null);
    setSlotStart("");
    setSlotEnd("");
    setModalError(null);
  };

  const openAddSlot = (plan: ParentMeetingOverview) => {
    setSlotModal({ type: "add", plan });
    setSlotStart("");
    setSlotEnd("");
    setModalError(null);
  };

  const openEditSlot = (plan: ParentMeetingOverview, slot: ParentMeetingSlot) => {
    setSlotModal({ type: "edit", plan, slot });
    setSlotStart(toDateTimeInputValue(getSlotStart(slot)));
    setSlotEnd(toDateTimeInputValue(getSlotEnd(slot)));
    setModalError(null);
  };

  const submitSlotForm = async () => {
    if (!slotModal || (slotModal.type !== "add" && slotModal.type !== "edit")) return;
    const start = new Date(slotStart);
    const end = new Date(slotEnd);
    if (!slotStart || !slotEnd || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      setModalError(t("parentMeetings.slot_form_required", { defaultValue: "Start and end time are required." }));
      return;
    }
    if (!end.getTime() || end <= start) {
      setModalError(t("parentMeetings.slot_form_invalid_range", { defaultValue: "End time must be after start time." }));
      return;
    }

    setSubmitting(true);
    setModalError(null);
    try {
      const payload = {
        startTime: toRequestDateTime(slotStart),
        endTime: toRequestDateTime(slotEnd),
      };
      if (slotModal.type === "add") {
        await http.post(`/parent-meetings/${getPlanId(slotModal.plan)}/slots`, payload);
      } else {
        await http.patch(`/parent-meetings/slots/${getSlotId(slotModal.slot)}`, payload);
      }
      closeSlotModal();
      await loadMeetings();
    } catch (err: any) {
      setModalError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          t("parentMeetings.slot_save_failed", { defaultValue: "Time slot could not be saved." })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const deleteSlot = async () => {
    if (!slotModal || slotModal.type !== "delete") return;
    setSubmitting(true);
    setModalError(null);
    try {
      await http.delete(`/parent-meetings/slots/${getSlotId(slotModal.slot)}`);
      closeSlotModal();
      await loadMeetings();
    } catch (err: any) {
      setModalError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          t("parentMeetings.slot_delete_failed", { defaultValue: "Time slot could not be deleted." })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const cancelMeeting = async () => {
    if (!slotModal || slotModal.type !== "cancel") return;
    setSubmitting(true);
    setModalError(null);
    try {
      await http.post(`/parent-meetings/slots/${getSlotId(slotModal.slot)}/cancel`, {});
      closeSlotModal();
      await loadMeetings();
    } catch (err: any) {
      setModalError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          t("parentMeetings.meeting_cancel_failed", { defaultValue: "Meeting could not be cancelled." })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const deletePlan = async () => {
    if (!slotModal || slotModal.type !== "deletePlan") return;
    const plan = slotModal.plan;
    const hasPlannedMeetings = (plan.plannedMeetings || []).length > 0;
    setSubmitting(true);
    setModalError(null);
    try {
      await http.delete(`/parent-meetings/${getPlanId(plan)}`, {
        params: hasPlannedMeetings ? { cancelBooked: true } : undefined,
      });
      closeSlotModal();
      await loadMeetings();
    } catch (err: any) {
      setModalError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          t("parentMeetings.plan_delete_failed", { defaultValue: "Parent meeting plan could not be deleted." })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderSlotRow = (plan: ParentMeetingOverview, slot: ParentMeetingSlot) => {
    const start = getSlotStart(slot);
    const end = getSlotEnd(slot);
    const timeLabel = [formatTime(start), formatTime(end)].filter(Boolean).join(" - ");
    const unknownTimeLabel = t("parentMeetings.unknown_time", { defaultValue: "Unknown time" });
    const isOpen = isAvailableSlot(slot);

    return (
      <div
        key={getSlotId(slot) || `${String(start)}-${slot.parentName || slot.childName || slot.status || "slot"}`}
        className="grid gap-2 rounded-xl border border-white/70 bg-white/70 px-3 py-2.5 text-sm shadow-sm dark:border-gray-700/70 dark:bg-gray-900/35 sm:grid-cols-12 sm:items-center"
      >
        <div className="sm:col-span-3">
          <p className="font-semibold text-gray-900 dark:text-white">{timeLabel || unknownTimeLabel}</p>
        </div>

        <div className="min-w-0 sm:col-span-3">
          {isOpen ? (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <IconClock className="h-4 w-4 text-sky-400" />
              <span>{t("parentMeetings.open_slot_short", { defaultValue: "Available" })}</span>
            </div>
          ) : (
            <p className="truncate font-semibold text-gray-900 dark:text-white">
              {slot.childName || t("parentMeetings.unknown_child", { defaultValue: "Unknown child" })}
            </p>
          )}
        </div>

        <div className="min-w-0 sm:col-span-3">
          {!isOpen ? (
            <div className="flex min-w-0 items-center gap-2 text-gray-600 dark:text-gray-300">
              <IconUser className="h-4 w-4 text-emerald-400" />
              <span className="truncate">
                {[slot.parentName, slot.parentEmail].filter(Boolean).join(" • ") ||
                  t("parentMeetings.no_parent", { defaultValue: "No parent details" })}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <span className="h-2 w-2 rounded-full bg-sky-400" />
              <span>{t("parentMeetings.no_booking", { defaultValue: "No booking" })}</span>
            </div>
          )}
        </div>

        <div className="sm:col-span-3">
          <div className="flex flex-wrap items-center gap-1.5 sm:justify-end">
            {!isOpen && (
              <span className="lumen-glow-sheen inline-flex rounded-full border border-emerald-200/70 bg-emerald-50/80 px-3 py-1 text-xs font-semibold text-emerald-700 dark:border-emerald-500/50 dark:bg-emerald-950/40 dark:text-emerald-200">
                {statusLabel(slot.status)}
              </span>
            )}
            {isOpen && canManageParentMeetings && (
              <>
                <button
                  type="button"
                  onClick={() => openEditSlot(plan, slot)}
                  className="lumen-glow-sheen inline-flex items-center gap-1 rounded-full border border-indigo-200/70 bg-indigo-50/80 px-3 py-1 text-xs font-semibold text-indigo-700 transition hover:text-indigo-900 dark:border-indigo-500/50 dark:bg-indigo-950/40 dark:text-indigo-200"
                >
                  <IconEdit className="h-3.5 w-3.5" />
                  {t("common.edit", { defaultValue: "Edit" })}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSlotModal({ type: "delete", plan, slot });
                    setModalError(null);
                  }}
                  className="lumen-glow-sheen inline-flex items-center gap-1 rounded-full border border-red-200/70 bg-red-50/80 px-3 py-1 text-xs font-semibold text-red-700 transition hover:text-red-900 dark:border-red-500/40 dark:bg-red-900/30 dark:text-red-200"
                >
                  <IconTrash className="h-3.5 w-3.5" />
                  {t("common.delete", { defaultValue: "Delete" })}
                </button>
              </>
            )}
            {!isOpen && canManageParentMeetings && (
              <button
                type="button"
                onClick={() => {
                  setSlotModal({ type: "cancel", plan, slot });
                  setModalError(null);
                }}
                className="lumen-glow-sheen inline-flex items-center gap-1 rounded-full border border-amber-200/70 bg-amber-50/80 px-3 py-1 text-xs font-semibold text-amber-800 transition hover:text-amber-900 dark:border-amber-500/50 dark:bg-amber-900/30 dark:text-amber-200"
              >
                <IconCancel className="h-3.5 w-3.5" />
                {t("common.cancel", { defaultValue: "Cancel" })}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSlotGroups = (plan: ParentMeetingOverview, slots: ParentMeetingSlot[]) => {
    const unknownTimeLabel = t("parentMeetings.unknown_time", { defaultValue: "Unknown time" });
    const planId = getPlanId(plan);

    const groups: EnrichedSlotDateGroup[] = groupSlotsByDate(slots).map((group) => ({
      ...group,
      openCount: group.slots.filter(isAvailableSlot).length,
      plannedCount: group.slots.filter((slot) => !isAvailableSlot(slot)).length,
    }));

    return groups.map((group) => {
      const open = isDayOpen(planId, group);

      return (
        <div
          key={`${group.dateLabel}-${group.sortTime}`}
          className="rounded-2xl border border-white/70 bg-white/50 p-3 shadow-sm dark:border-gray-700/60 dark:bg-gray-900/25"
        >
          <button
            type="button"
            onClick={() => toggleDay(planId, group)}
            className="flex w-full flex-wrap items-center justify-between gap-3 text-left"
          >
            <div className="flex items-center gap-2">
              {open ? (
                <IconChevronDown className="h-4 w-4 text-indigo-500" />
              ) : (
                <IconChevronRight className="h-4 w-4 text-indigo-500" />
              )}
              <p className="text-sm font-bold text-gray-800 dark:text-gray-100">
                {group.dateLabel === "Unknown time" ? unknownTimeLabel : group.dateLabel}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="rounded-full border border-sky-200/70 bg-sky-50/80 px-3 py-1 text-xs font-semibold text-sky-700 dark:border-sky-500/50 dark:bg-sky-900/40 dark:text-sky-200">
                {t("parentMeetings.open_slots_count", {
                  defaultValue: "{{count}} open slots",
                  count: group.openCount,
                })}
              </span>
              <span className="rounded-full border border-emerald-200/70 bg-emerald-50/80 px-3 py-1 text-xs font-semibold text-emerald-700 dark:border-emerald-500/50 dark:bg-emerald-950/40 dark:text-emerald-200">
                {t("parentMeetings.planned_meetings_count", {
                  defaultValue: "{{count}} planned meetings",
                  count: group.plannedCount,
                })}
              </span>
            </div>
          </button>

          {open && (
            <div className="mt-3 space-y-1.5">
              {group.slots.map((slot) => renderSlotRow(plan, slot))}
            </div>
          )}
        </div>
      );
    });
  };

  const renderEmptySection = (message: string) => (
    <p className="rounded-xl border border-white/70 bg-white/60 px-4 py-4 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900/35 dark:text-gray-400">
      {message}
    </p>
  );

  return (
    <LdsOverviewPage styles={<style>{parentMeetingsStyles}</style>}>
      <div className={lumenPageShell}>
        <LdsOverviewHero
          icon={FiCalendar}
          eyebrow={t("myEnvironment.eyebrow", { defaultValue: "Personal workspace" })}
          title={t("parentMeetings.title", { defaultValue: "Parent meetings" })}
          description={t("myEnvironment.parentMeetings.description", {
            defaultValue: "View open slots, reservations and confirmed meetings.",
          })}
        />

        <section className="my-parent-meetings-enter rounded-[32px] border border-indigo-100/60 bg-white/90 p-4 shadow-[0_25px_70px_rgba(15,23,42,0.10)] backdrop-blur dark:border-indigo-500/40 dark:bg-gray-900/60 sm:p-5">
          <div className="relative">
            <IconSearch className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-indigo-400" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("parentMeetings.search_placeholder", {
                defaultValue: "Search plans, slots, children, parents, email, status or classrooms",
              })}
              className="lumen-glow-input w-full py-3 pl-11 pr-4 text-sm text-gray-900 placeholder:text-gray-500 dark:text-white"
            />
          </div>
        </section>

        {loading && (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        )}

        {!loading && error && (
          <div className="flex items-start gap-3 rounded-2xl border border-red-100/70 bg-gradient-to-br from-red-50/80 via-white to-white p-4 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:from-red-900/30 dark:via-gray-900/50 dark:to-gray-900/60 dark:text-red-200">
            <IconAlert className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <div>
              <p className="font-semibold">{error}</p>
              <button type="button" onClick={loadMeetings} className="mt-2 text-sm font-semibold underline underline-offset-2">
                {t("common.try_again", { defaultValue: "Try again" })}
              </button>
            </div>
          </div>
        )}

        {!loading && !error && filteredPlans.length === 0 && (
          <div className="rounded-[32px] border border-indigo-100/60 bg-gradient-to-br from-indigo-50/80 via-white to-white p-8 text-center shadow-[0_25px_70px_rgba(15,23,42,0.12)] backdrop-blur dark:border-indigo-500/40 dark:from-indigo-950/30 dark:via-gray-900/50 dark:to-gray-900/60">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-indigo-100/70 bg-white/75 text-indigo-500 dark:border-indigo-500/40 dark:bg-gray-900/40 dark:text-indigo-200">
              <IconCalendar className="h-6 w-6" />
            </div>
            <h2 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("parentMeetings.empty_title", { defaultValue: "No meetings found" })}
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {query
                ? t("parentMeetings.empty_search", { defaultValue: "No parent meetings match your search." })
                : t("parentMeetings.empty", { defaultValue: "There are no parent meetings planned yet." })}
            </p>
          </div>
        )}

        {!loading && !error && filteredPlans.length > 0 && (
          <div className="space-y-5">
            {filteredPlans.map((plan) => {
              const planId = getPlanId(plan);
              const planClassroomId = getClassroomId(plan);
              const openSlots = dedupeSlotsForDisplay(plan.openSlots || [], planId, true);
              const plannedMeetings = plan.plannedMeetings || [];
              const agendaSlots = [...openSlots, ...plannedMeetings].sort((left, right) => slotSortTime(left) - slotSortTime(right));

              return (
                <section
                  key={planId || plan.title}
                  className={`my-parent-meetings-enter ${lumenPanel} overflow-hidden p-5 sm:p-6`}
                  style={{ animationDelay: `${Math.min(80 + filteredPlans.indexOf(plan) * 60, 360)}ms` }}
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {plan.title || t("parentMeetings.untitled_plan", { defaultValue: "Untitled plan" })}
                      </h2>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {getDisplayClassroomName(plan) || t("parentMeetings.no_classroom", { defaultValue: "No classroom" })}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                      <span className="lumen-glow-sheen rounded-full border border-gray-200/70 bg-white/75 px-3 py-1 text-xs font-semibold text-gray-600 dark:border-gray-600/40 dark:bg-gray-900/40 dark:text-gray-300">
                        {statusLabel(plan.status)}
                      </span>
                      <span className="lumen-glow-sheen rounded-full border border-sky-200/70 bg-sky-50/80 px-3 py-1 text-xs font-semibold text-sky-700 dark:border-sky-500/50 dark:bg-sky-900/40 dark:text-sky-200">
                        {t("parentMeetings.open_slots_count", {
                          defaultValue: "{{count}} open slots",
                          count: openSlots.length,
                        })}
                      </span>
                      <span className="lumen-glow-sheen rounded-full border border-emerald-200/70 bg-emerald-50/80 px-3 py-1 text-xs font-semibold text-emerald-700 dark:border-emerald-500/50 dark:bg-emerald-950/40 dark:text-emerald-200">
                        {t("parentMeetings.planned_meetings_count", {
                          defaultValue: "{{count}} planned meetings",
                          count: plannedMeetings.length,
                        })}
                      </span>
                      {canManageParentMeetings && (
                        <button
                          type="button"
                          onClick={() => openAddSlot(plan)}
                          className={`${lumenEmeraldButton} gap-2 px-4 py-2`}
                        >
                          <IconPlus className="h-4 w-4" />
                          {t("parentMeetings.add_time_slot", { defaultValue: "Add time slot" })}
                        </button>
                      )}
                      {canManageParentMeetings && planId && (
                        <button
                          type="button"
                          onClick={() => navigate(`/parent-meetings/${planId}`)}
                          className={lumenIndigoButton}
                        >
                          {t("parentMeetings.manage_plan", { defaultValue: "Manage plan" })}
                        </button>
                      )}
                      {planClassroomId && (
                        <button
                          type="button"
                          onClick={() => navigate(`/school/classrooms/${planClassroomId}/details`)}
                          className={lumenSkyButton}
                        >
                          {t("parentMeetings.open_classroom", { defaultValue: "Classroom" })}
                        </button>
                      )}
                      {canManageParentMeetings && planId && (
                        <button
                          type="button"
                          onClick={() => {
                            setSlotModal({ type: "deletePlan", plan });
                            setModalError(null);
                          }}
                          className="lumen-glow-sheen inline-flex items-center justify-center gap-2 rounded-full border border-red-200/70 bg-red-50/80 px-4 py-2 text-sm font-semibold text-red-700 transition hover:text-red-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-500/40 dark:bg-red-900/30 dark:text-red-200"
                        >
                          <IconTrash className="h-4 w-4" />
                          {t("parentMeetings.delete_plan", { defaultValue: "Delete plan" })}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-5 rounded-2xl border border-indigo-100/70 bg-gradient-to-br from-white/80 via-indigo-50/45 to-sky-50/60 p-4 shadow-sm backdrop-blur dark:border-indigo-500/35 dark:from-gray-900/50 dark:via-indigo-950/25 dark:to-sky-950/20">
                    {agendaSlots.length > 0 ? (
                      <div className="max-h-[32rem] space-y-4 overflow-y-auto pr-1">
                        {renderSlotGroups(plan, agendaSlots)}
                      </div>
                    ) : (
                      renderEmptySection(t("parentMeetings.no_agenda_items", {
                        defaultValue: "No time slots or planned meetings yet.",
                      }))
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        show={slotModal?.type === "add" || slotModal?.type === "edit"}
        title={
          slotModal?.type === "edit"
            ? t("parentMeetings.edit_time_slot", { defaultValue: "Edit time slot" })
            : t("parentMeetings.add_time_slot", { defaultValue: "Add time slot" })
        }
        type="manage"
        onClose={closeSlotModal}
        hideDefaultActions
        dialogClassName="w-[min(92vw,32rem)] max-w-none text-left"
        footer={
          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={closeSlotModal}
              disabled={submitting}
              className={lumenSecondaryButton}
            >
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="button"
              onClick={submitSlotForm}
              disabled={submitting}
              className={lumenEmeraldButton}
            >
              {submitting
                ? t("common.saving", { defaultValue: "Saving..." })
                : t("common.save", { defaultValue: "Save" })}
            </button>
          </div>
        }
      >
        <div className="space-y-4 text-left">
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {t("parentMeetings.slot_start", { defaultValue: "Start date/time" })}
            </span>
            <input
              type="datetime-local"
              value={slotStart}
              onChange={(event) => setSlotStart(event.target.value)}
              className="lumen-glow-input mt-1 w-full px-4 py-2 text-sm text-gray-900 dark:text-white"
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {t("parentMeetings.slot_end", { defaultValue: "End date/time" })}
            </span>
            <input
              type="datetime-local"
              value={slotEnd}
              onChange={(event) => setSlotEnd(event.target.value)}
              className="lumen-glow-input mt-1 w-full px-4 py-2 text-sm text-gray-900 dark:text-white"
            />
          </label>
          {modalError && (
            <p className="rounded-2xl border border-red-100/70 bg-gradient-to-br from-red-50/80 via-white to-white px-3 py-2 text-sm font-semibold text-red-700 dark:border-red-500/40 dark:from-red-900/30 dark:via-gray-900/50 dark:to-gray-900/60 dark:text-red-200">
              {modalError}
            </p>
          )}
        </div>
      </Modal>

      <Modal
        show={slotModal?.type === "delete"}
        title={t("parentMeetings.delete_time_slot", { defaultValue: "Delete time slot" })}
        type="confirm"
        onClose={closeSlotModal}
        onConfirm={deleteSlot}
        dialogClassName="w-[min(92vw,30rem)] max-w-none"
      >
        <div className="space-y-3 text-left text-sm text-gray-600 dark:text-gray-300">
          <p>
            {t("parentMeetings.delete_time_slot_confirm", {
              defaultValue: "Delete this available time slot?",
            })}
          </p>
          {slotModal?.type === "delete" && (
            <p className="font-semibold text-gray-900 dark:text-white">
              {formatDate(getSlotStart(slotModal.slot))} {formatTime(getSlotStart(slotModal.slot))} - {formatTime(getSlotEnd(slotModal.slot))}
            </p>
          )}
          {modalError && <p className="font-semibold text-red-600 dark:text-red-300">{modalError}</p>}
        </div>
      </Modal>

      <Modal
        show={slotModal?.type === "cancel"}
        title={t("parentMeetings.cancel_meeting", { defaultValue: "Cancel meeting" })}
        type="confirm"
        onClose={closeSlotModal}
        onConfirm={cancelMeeting}
        dialogClassName="w-[min(92vw,30rem)] max-w-none"
      >
        <div className="space-y-3 text-left text-sm text-gray-600 dark:text-gray-300">
          <p>
            {t("parentMeetings.cancel_meeting_confirm", {
              defaultValue: "Cancel this planned meeting?",
            })}
          </p>
          {slotModal?.type === "cancel" && (
            <div className="space-y-1 font-semibold text-gray-900 dark:text-white">
              <p>{slotModal.slot.childName || t("parentMeetings.unknown_child", { defaultValue: "Unknown child" })}</p>
              <p>{[slotModal.slot.parentName, slotModal.slot.parentEmail].filter(Boolean).join(" • ")}</p>
              <p>
                {formatDate(getSlotStart(slotModal.slot))} {formatTime(getSlotStart(slotModal.slot))} - {formatTime(getSlotEnd(slotModal.slot))}
              </p>
            </div>
          )}
          {modalError && <p className="font-semibold text-red-600 dark:text-red-300">{modalError}</p>}
        </div>
      </Modal>

      <Modal
        show={slotModal?.type === "deletePlan"}
        title={t("parentMeetings.delete_plan", { defaultValue: "Delete plan" })}
        type="confirm"
        onClose={closeSlotModal}
        onConfirm={deletePlan}
        dialogClassName="w-[min(92vw,32rem)] max-w-none"
      >
        <div className="space-y-3 text-left text-sm text-gray-600 dark:text-gray-300">
          <p>
            {t("parentMeetings.delete_plan_confirm", {
              defaultValue: "Delete this parent meeting plan and all available time slots?",
            })}
          </p>
          {slotModal?.type === "deletePlan" && (slotModal.plan.plannedMeetings || []).length > 0 && (
            <p className="rounded-2xl border border-amber-100/70 bg-amber-50/80 px-3 py-2 font-semibold text-amber-800 dark:border-amber-500/40 dark:bg-amber-900/30 dark:text-amber-200">
              {t("parentMeetings.delete_plan_booked_warning", {
                defaultValue: "Booked meetings will be cancelled and parents will be notified.",
              })}
            </p>
          )}
          {slotModal?.type === "deletePlan" && (
            <div className="space-y-1 font-semibold text-gray-900 dark:text-white">
              <p>{slotModal.plan.title || t("parentMeetings.untitled_plan", { defaultValue: "Untitled plan" })}</p>
              <p>{getDisplayClassroomName(slotModal.plan) || t("parentMeetings.no_classroom", { defaultValue: "No classroom" })}</p>
            </div>
          )}
          {modalError && <p className="font-semibold text-red-600 dark:text-red-300">{modalError}</p>}
        </div>
      </Modal>
    </LdsOverviewPage>
  );
};

export default MyParentMeetings;
